/* eslint-disable jsx-a11y/alt-text */
import * as React from "react";
import { Box } from "@mui/material";
import { InsuranceCompany } from "../../../redux/types/InsuranceCompany";
import { TxtInput } from "../../../components";
import { Prompt } from "../../../components/dialogue/Dialogue";
import { useMutation } from "@apollo/client";
import { DELETE_INSURANCE_COMPANY_BY_ID } from "../../../apolloClient/operation/mutations/mutation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logEvent, INSURANCE_EVENTS } from "../../../utils/amplitude";
import searchIcon from "../../../images/searchIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

import styled from "@emotion/styled";

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
`;

const AddButton = styled(ActionButton)`
  background-color: #4b87ed;
  color: white;
  width: 100%;

  &:hover {
    background-color: #3a6fd8;
  }

  @media only screen and (min-width: 768px) {
    width: auto;
  }
`;

const EditButton = styled(ActionButton)`
  background-color: #e5e7eb;
  color: #374151;

  &:hover {
    background-color: #d1d5db;
  }
`;

const DeleteButton = styled(ActionButton)`
  background-color: #fee2e2;
  color: #dc2626;

  &:hover {
    background-color: #fecaca;
  }
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  object-fit: contain;
  background-color: #f3f4f6;
  padding: 4px;
`;

const LinkText = styled.a`
  color: #4b87ed;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

interface UserTableProps {
  insuranceCompanies: InsuranceCompany[];
  onTapAddCompany: Function;
  onEditInsurance: (insurance: InsuranceCompany) => void;
  refetch?: Function;
  permissions?: boolean;
}

export const InsuranceTable: React.FC<UserTableProps> = ({
  insuranceCompanies,
  onTapAddCompany,
  onEditInsurance,
  refetch,
  permissions = true,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [keyword, setKeyword] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);
  const [openConfirmDelete, setOpenConfirmDelete] = React.useState<boolean>(false);
  const [insuranceIdToDelete, setInsuranceIdToDelete] = React.useState<number | null>(null);
  const [insuranceCompanyNameToDelete, setInsuranCompanyNameToDelete] = React.useState<string>("");

  const [deleteInsuranceCompany] = useMutation<
    { deleteInsuranceCompany: Boolean },
    { id: Number }
  >(DELETE_INSURANCE_COMPANY_BY_ID);

  const filterInsuranceCompanies = () => {
    if (keyword.length > 0 && Array.isArray(insuranceCompanies)) {
      return insuranceCompanies.filter((item) => {
        return item.companyName.toLowerCase().includes(keyword.toLowerCase());
      });
    }
    return insuranceCompanies;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filterInsuranceCompanies().length / rowsPerPage);
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handleDeleteInsuranceCompanyById = async (id: number) => {
    try {
      const { data } = await deleteInsuranceCompany({
        variables: { id: id },
      });
      if (data?.deleteInsuranceCompany) {
        refetch?.();
        logEvent(INSURANCE_EVENTS.COMPANY_DELETED, {
          company_id: id,
          company_name: insuranceCompanyNameToDelete,
        });
        setInsuranceIdToDelete(null);
        setOpenConfirmDelete(false);
        toast("Insurance company deleted successfully!", {
          type: "success",
          className: "toast-message",
        });
      }
    } catch (error) {
      console.error("Error deleting insurance company:", error);
      setInsuranceIdToDelete(null);
      setOpenConfirmDelete(false);
      toast("Error deleting insurance company!", {
        type: "error",
        className: "toast-message",
      });
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setInsuranceIdToDelete(null);
    setInsuranCompanyNameToDelete("");
  };

  const filteredCompanies = filterInsuranceCompanies();
  const paginatedCompanies = filteredCompanies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filteredCompanies.length / rowsPerPage);

  return (
    <PageContainer>
      <Prompt
        message={`Delete ${insuranceCompanyNameToDelete} insurance company?`}
        onConfirm={() => handleDeleteInsuranceCompanyById(insuranceIdToDelete!)}
        onClose={handleCancelDelete}
        open={openConfirmDelete}
      />

      <ContentWrapper>
        <HeaderSection>
          <TitleRow>
            <PageTitle>Insurance Providers</PageTitle>
          </TitleRow>
          <PageSubtitle>{filteredCompanies.length} total providers</PageSubtitle>
        </HeaderSection>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search providers..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>

          {permissions && (
            <AddButton onClick={() => onTapAddCompany()}>+ Add Company</AddButton>
          )}
        </Box>

        <TableWrapper>
          <TableHeader>
            <TableHeaderCell width="35%">Company Name</TableHeaderCell>
            <TableHeaderCell width="15%">Logo</TableHeaderCell>
            <TableHeaderCell width="25%">Claim Link</TableHeaderCell>
            <TableHeaderCell width="25%">Actions</TableHeaderCell>
          </TableHeader>

          {filteredCompanies.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No insurance companies found</EmptyStateText>
            </EmptyState>
          ) : (
            paginatedCompanies.map((company) => (
              <TableRow key={company.id}>
                <PrimaryCell width="35%" mobileLabel="Company">
                  {company.companyName}
                </PrimaryCell>
                <TableCell width="15%" mobileLabel="Logo">
                  {company.companyLogo && (
                    <LogoImage src={company.companyLogo} alt={company.companyName} />
                  )}
                </TableCell>
                <TableCell width="25%" mobileLabel="Claim Link">
                  {company.claimLink && (
                    <LinkText href={company.claimLink} target="_blank" rel="noopener noreferrer">
                      Visit site →
                    </LinkText>
                  )}
                </TableCell>
                <TableCell width="25%">
                  {permissions && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <EditButton onClick={() => onEditInsurance(company)}>Edit</EditButton>
                      <DeleteButton
                        onClick={() => {
                          setOpenConfirmDelete(true);
                          setInsuranceIdToDelete(company.id);
                          setInsuranCompanyNameToDelete(company.companyName);
                        }}
                      >
                        Delete
                      </DeleteButton>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}

          {filteredCompanies.length > 0 && totalPages > 1 && (
            <PaginationWrapper>
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <PaginationText>
                Page {page + 1} of {totalPages}
              </PaginationText>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </PaginationWrapper>
          )}
        </TableWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};
